import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, ShieldAlert, Sparkles, Clock,
  MessageCircle, Phone, MapPin, CalendarCheck, Info, RotateCcw,
  Mic, MicOff, Camera, X as XIcon,
} from "lucide-react";
import axios from "axios";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { CLINIC, waLink } from "../lib/clinic";
import { SITE, breadcrumb, graph } from "../lib/schema";
import {
  AGE_BANDS, GENDERS, EYE_SIDES, SYMPTOMS, FOLLOWUPS, MEDICAL_HISTORY, WIZARD_STRINGS,
} from "../data/symptomCheckerData";
import { SC_CREDIT_LINE, SC_DISCLAIMER_SHORT, SC_DISCLAIMER_LONG } from "../data/symptomCheckerCredits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/symptom-checker`;

const CREDIT_LINE = SC_CREDIT_LINE;
const DISCLAIMER = SC_DISCLAIMER_SHORT;
const DISCLAIMER_LONG = SC_DISCLAIMER_LONG;

const BADGE_STYLES = {
  emergency: { bg: "bg-red-500/12", ring: "ring-red-500/30", text: "text-red-500", pill: "bg-red-500" },
  urgent:    { bg: "bg-orange-500/12", ring: "ring-orange-500/30", text: "text-orange-500", pill: "bg-orange-500" },
  soon:      { bg: "bg-amber-500/12", ring: "ring-amber-500/30", text: "text-amber-500", pill: "bg-amber-500" },
  routine:   { bg: "bg-emerald-500/12", ring: "ring-emerald-500/30", text: "text-emerald-500", pill: "bg-emerald-500" },
};

/* ─────────────────────────── UI primitives ─────────────────────────── */

const StepShell = ({ stepNumber, totalSteps, title, hint, children, onBack, onNext, nextDisabled, nextLabel, T }) => (
  <div className="max-w-3xl mx-auto" data-testid={`step-shell-${stepNumber}`}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--accent))] mb-3">
      <Sparkles size={12} strokeWidth={1.8} /> {T.stepOf(stepNumber, totalSteps)}
    </div>
    <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-balance text-[hsl(var(--foreground))]">
      {title}
    </h2>
    {hint && <p className="mt-3 text-[15px] text-[hsl(var(--foreground))]/70">{hint}</p>}
    <div className="mt-10">{children}</div>
    <div className="mt-12 flex items-center justify-between">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/60 hover:text-[hsl(var(--foreground))] transition-colors"
          data-testid="btn-back"
        >
          <ArrowLeft size={13} strokeWidth={1.6} /> {T.back}
        </button>
      ) : <span />}
      {onNext && (
        <button
          type="button"
          onClick={onNext}
          disabled={!!nextDisabled}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="btn-next"
        >
          {nextLabel || T.continue} <ArrowRight size={16} strokeWidth={1.6} />
        </button>
      )}
    </div>
  </div>
);

const OptionCard = ({ selected, onClick, icon: Icon, label, testid, danger }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left w-full rounded-2xl px-5 py-4 border transition-all duration-200 flex items-center gap-4 group
      ${selected
        ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.05] shadow-[0_0_0_1px_hsl(var(--primary))]"
        : "border-border hover:border-[hsl(var(--foreground))]/30 hover:bg-[hsl(var(--foreground))]/[0.02]"}
    `}
  >
    {Icon && (
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0
        ${danger ? "bg-red-500/10 text-red-500" : "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]"}`}>
        <Icon size={19} strokeWidth={1.7} />
      </span>
    )}
    <span className={`text-[15px] ${selected ? "text-[hsl(var(--primary))] font-medium" : "text-[hsl(var(--foreground))]/90"}`}>
      {label}
    </span>
    {selected && <Check size={16} className="ml-auto text-[hsl(var(--primary))]" strokeWidth={2} />}
  </button>
);

const ProgressRail = ({ current, total }) => (
  <div className="w-full h-[3px] rounded-full bg-[hsl(var(--foreground))]/10 overflow-hidden">
    <div
      className="h-full bg-[hsl(var(--accent))] transition-all duration-500 ease-out"
      style={{ width: `${(current / total) * 100}%` }}
    />
  </div>
);

/* ─────────────────────────── main page ─────────────────────────── */

const TOTAL_STEPS = 6;

const SymptomChecker = () => {
  usePageMeta({
    title: "AI Eye Symptom Checker | Shantanu Netralaya, Varanasi",
    path: "/symptom-checker",
    description:
      "Understand the urgency of your eye symptoms in less than 60 seconds. Educational triage tool built by an ophthalmologist. Not a diagnostic replacement.",
  });

  usePageJsonLd(graph([
    breadcrumb([
      { name: "Home", path: "/" },
      { name: "AI Eye Symptom Checker", path: "/symptom-checker" },
    ]),
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE}/symptom-checker#page`,
      url: `${SITE}/symptom-checker`,
      name: "AI Eye Symptom Checker",
      description:
        "Educational ophthalmic triage assistant. Classifies symptom urgency (emergency, urgent, soon or routine) and suggests when to seek eye care.",
      inLanguage: "en-IN",
      medicalAudience: "https://schema.org/Patient",
    },
  ]));

  // "landing" -> "wizard" -> "loading" -> "result"
  const [phase, setPhase] = useState("landing");
  const [step, setStep] = useState(1);
  const [selectedLang, setSelectedLang] = useState(null); // "en" | "hi" — picked on landing
  const [state, setState] = useState({
    age_band: null,
    gender: null,
    eye_side: null,
    main_symptom: null,
    followups: {},
    medical_history: [],
    notes: "",           // patient's own words (voice or typed)
    photo_url: null,     // optional uploaded eye photo
  });
  const [loading, setLoading] = useState(false);
  const [loadingSecondsLeft, setLoadingSecondsLeft] = useState(30);
  const [result, setResult] = useState(null);
  const [listening, setListening] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const recognitionRef = useRef(null);

  // Lang-aware string bundle. Falls back to English if selectedLang is null.
  const T = WIZARD_STRINGS[selectedLang === "hi" ? "hi" : "en"];
  const isHi = selectedLang === "hi";
  const tr = (obj) => (isHi ? (obj.hi || obj.label) : obj.label);
  const trQ = (f)  => (isHi ? (f.hi_q || f.q) : f.q);

  // Web Speech API — feature-detect and reuse a single instance.
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = (document.documentElement.lang || "").startsWith("hi") ? "hi-IN" : "en-IN";
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (ev) => {
      const t = Array.from(ev.results).map((res) => res[0]?.transcript || "").join(" ").trim();
      if (t) setState((s) => ({ ...s, notes: s.notes ? s.notes + " " + t : t }));
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recognitionRef.current = r;
    return () => { try { r.abort(); } catch { /* noop */ } };
  }, []);

  const toggleMic = () => {
    const r = recognitionRef.current;
    if (!r) return;
    if (listening) { try { r.stop(); } catch { /* noop */ } setListening(false); }
    else { try { r.start(); setListening(true); } catch { /* noop */ } }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) setState((s) => ({ ...s, photo_url: data.url }));
    } catch { /* silent — photo is optional */ }
    finally { setUploadingPhoto(false); }
  };

  const patch = (partial) => setState((s) => ({ ...s, ...partial }));
  const patchFollowup = (key, value) => setState((s) => ({ ...s, followups: { ...s.followups, [key]: value } }));

  const goto = (n) => {
    setStep(Math.max(1, Math.min(TOTAL_STEPS, n)));
    // Scroll to top on every step change so the next question is in view on mobile too.
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 60);
  };

  const activeSymptomFollowups = state.main_symptom ? (FOLLOWUPS[state.main_symptom] || []) : [];

  const submit = async () => {
    setLoading(true);
    setPhase("loading");
    setLoadingSecondsLeft(30);
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Kick off a 1s-tick countdown. We never abort even if the timer hits 0
    // — the actual triage call keeps running.
    const started = Date.now();
    const tickId = setInterval(() => {
      const elapsed = Math.floor((Date.now() - started) / 1000);
      setLoadingSecondsLeft(Math.max(0, 30 - elapsed));
    }, 1000);
    try {
      const lang = selectedLang || ((document.documentElement.lang || "").startsWith("hi") ? "hi" : "en");
      const { data } = await axios.post(`${API}/triage`, {
        age_band: state.age_band,
        gender: state.gender,
        eye_side: state.eye_side,
        main_symptom: state.main_symptom,
        followups: { ...state.followups, patient_notes: state.notes || undefined },
        medical_history: state.medical_history,
        photo_url: state.photo_url,
        lang,
      });
      setResult(data);
      setPhase("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setResult({
        urgency: "soon",
        urgency_label: T.fallback_label,
        badge_color: "amber",
        summary: T.fallback_summary,
        possible_conditions: [],
        why_it_matters: T.fallback_why,
        recommended_action: T.fallback_action,
        warning_signs: T.fallback_warnings,
        red_flag_triggered: false,
        disclaimer: DISCLAIMER,
      });
      setPhase("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
      clearInterval(tickId);
    }
  };

  const restart = () => {
    setPhase("landing");
    setStep(1);
    setState({ age_band: null, gender: null, eye_side: null, main_symptom: null, followups: {}, medical_history: [] });
    setResult(null);
  };

  const logCta = async (cta) => {
    try {
      await axios.post(`${API}/log-click`, { cta, assessment_id: result?.assessment_id });
    } catch { /* best-effort */ }
  };

  /* ─────────────────────────── LANDING ─────────────────────────── */
  if (phase === "landing") {
    return (
      <main className="pb-24" data-testid="symptom-checker-landing">
        <section className="pt-16 sm:pt-24 pb-14 container-page">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">
              <Sparkles size={13} strokeWidth={1.8} /> AI Eye Symptom Checker
            </div>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance" data-testid="checker-h1">
              Understand the urgency of your eye symptoms in less than 60 seconds.
            </h1>
            <p className="mt-8 prose-elegant mx-auto max-w-2xl">
              An educational triage assistant built by an ophthalmologist. It does not diagnose — it helps you decide
              how quickly to seek eye care, in plain language.
            </p>
            <div className="mt-10 flex flex-col items-center gap-4">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--foreground))]/55">
                Choose your language to begin
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  className="btn-primary"
                  onClick={() => {
                    setSelectedLang("en");
                    document.documentElement.lang = "en-IN";
                    setPhase("wizard"); setStep(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid="btn-start-english"
                >
                  Start in English <ArrowRight size={16} strokeWidth={1.6} />
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setSelectedLang("hi");
                    document.documentElement.lang = "hi-IN";
                    try { localStorage.setItem("lang", "hi"); } catch { /* ignore */ }
                    setPhase("wizard"); setStep(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid="btn-start-hindi"
                  style={{ fontFamily: '"Noto Sans Devanagari", "Hind", system-ui' }}
                >
                  हिन्दी में शुरू करें <ArrowRight size={16} strokeWidth={1.6} />
                </button>
              </div>
              <a href={waLink("Hi, I would like to book an appointment at Shantanu Netralaya.")} target="_blank" rel="noopener noreferrer" className="btn-ghost mt-2" data-testid="btn-book-appointment-hero">
                Book Appointment
              </a>
            </div>
            <p className="mt-4 text-[12px] text-[hsl(var(--foreground))]/60 max-w-xl mx-auto">
              <MessageCircle size={12} strokeWidth={1.8} className="inline-block mr-1 -mt-0.5 text-[hsl(var(--accent))]" />
              Prefer to skip the tool? WhatsApp our team directly — Dr. Shantanu &amp; Dr. Manisha personally review incoming messages and reply within 24 hours.
            </p>
            <ul className="mt-14 grid sm:grid-cols-3 gap-x-6 gap-y-4 text-[13px] text-[hsl(var(--foreground))]/80">
              {[
                "Educational only",
                "Under 60 seconds",
                "No login required",
                "Created by an ophthalmologist",
                "Mobile friendly",
                "Anonymous & private",
              ].map((t) => (
                <li key={t} className="inline-flex items-center justify-center gap-2">
                  <Check size={14} className="text-[hsl(var(--accent))]" strokeWidth={2.2} /> {t}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="container-page">
          <div className="max-w-3xl mx-auto rounded-2xl border border-border bg-[hsl(var(--foreground))]/[0.02] px-6 py-5 flex items-start gap-3">
            <Info size={16} className="mt-1 text-[hsl(var(--accent))] shrink-0" strokeWidth={1.8} />
            <p className="text-[13px] leading-relaxed text-[hsl(var(--foreground))]/75">
              <span className="font-medium text-[hsl(var(--foreground))]">Important:</span> {DISCLAIMER}
              <span className="block mt-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50">{CREDIT_LINE}</span>
            </p>
          </div>
        </section>
      </main>
    );
  }

  /* ─────────────────────────── LOADING ─────────────────────────── */
  if (phase === "loading") {
    const pct = Math.max(0, Math.min(100, ((30 - loadingSecondsLeft) / 30) * 100));
    const overtime = loadingSecondsLeft <= 0;
    return (
      <main className="pb-24" data-testid="symptom-checker-loading">
        <section className="pt-20 sm:pt-28 pb-14 container-page">
          <div className="max-w-xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">
              <Sparkles size={13} strokeWidth={1.8} /> {T.loading_kicker}
            </div>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl leading-[1.1] text-balance text-[hsl(var(--foreground))]">
              {overtime ? T.loading_title_overtime : T.loading_title}
            </h2>
            <p className="mt-5 text-[14px] text-[hsl(var(--foreground))]/65 leading-relaxed">
              {T.loading_hint}
            </p>

            {/* Countdown ring */}
            <div className="mt-12 relative w-40 h-40 mx-auto">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="45" strokeWidth="6"
                        className="fill-none stroke-[hsl(var(--foreground))]/10" />
                <circle cx="50" cy="50" r="45" strokeWidth="6" strokeLinecap="round"
                        className="fill-none stroke-[hsl(var(--accent))] transition-[stroke-dashoffset] duration-500 ease-out"
                        style={{
                          strokeDasharray: `${2 * Math.PI * 45}`,
                          strokeDashoffset: `${2 * Math.PI * 45 * (1 - pct / 100)}`,
                        }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-serif text-4xl text-[hsl(var(--foreground))]" data-testid="loading-countdown">
                  {overtime ? "…" : loadingSecondsLeft}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/50 mt-1">
                  {overtime ? T.loading_wrapping : T.loading_seconds}
                </div>
              </div>
            </div>

            <ul className="mt-12 text-left space-y-2.5 text-[13.5px] text-[hsl(var(--foreground))]/75 max-w-md mx-auto">
              {T.loading_bullets.map((b) => (
                <li key={b} className="flex items-start gap-2"><Check size={14} className="mt-1 text-[hsl(var(--accent))]" strokeWidth={2.4}/> {b}</li>
              ))}
            </ul>

            <p className="mt-12 text-[11.5px] leading-relaxed text-[hsl(var(--foreground))]/60">
              {DISCLAIMER_LONG}
              <span className="block mt-1 text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/45">{CREDIT_LINE}</span>
            </p>
          </div>
        </section>
      </main>
    );
  }

  /* ─────────────────────────── RESULT ─────────────────────────── */
  if (phase === "result" && result) {
    const s = BADGE_STYLES[result.urgency] || BADGE_STYLES.soon;
    return (
      <main className="pb-24" data-testid="symptom-checker-result">
        <section className="pt-14 sm:pt-20 pb-10 container-page">
          <div className="max-w-3xl mx-auto">
            <div className={`rounded-3xl ring-1 ${s.ring} ${s.bg} p-8 sm:p-10`} data-testid="result-card">
              <div className="flex items-center gap-3 mb-3">
                <span className={`w-2.5 h-2.5 rounded-full ${s.pill} animate-pulse`} />
                <span className={`text-[11px] uppercase tracking-[0.24em] ${s.text} font-medium`}>
                  {result.urgency}
                </span>
                {result.red_flag_triggered && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-red-500 border border-red-500/30 rounded-full px-2 py-0.5 ml-auto">
                    <ShieldAlert size={11} strokeWidth={2} /> {T.result_redflag}
                  </span>
                )}
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl leading-tight text-balance text-[hsl(var(--foreground))]" data-testid="result-summary">
                {result.summary}
              </h2>
              <div className="mt-5 inline-flex items-center gap-2 text-[13px] text-[hsl(var(--foreground))]/80">
                <Clock size={14} strokeWidth={1.7} />
                {result.urgency_label}
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-border p-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-3">{T.result_causes}</div>
                <ul className="space-y-1.5 text-[14px] text-[hsl(var(--foreground))]/85">
                  {(result.possible_conditions || []).map((c) => (
                    <li key={c} className="flex items-start gap-2">
                      <span className={`mt-2 w-1 h-1 rounded-full ${s.pill}`} /> {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-border p-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-3">{T.result_matters}</div>
                <p className="text-[14px] leading-relaxed text-[hsl(var(--foreground))]/85">{result.why_it_matters}</p>
              </div>
              <div className="rounded-2xl border border-border p-5 sm:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-3">{T.result_watch}</div>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-[14px] text-[hsl(var(--foreground))]/85">
                  {(result.warning_signs || []).map((w) => (
                    <li key={w} className="flex items-start gap-2">
                      <ShieldAlert size={13} className="mt-1 text-red-500 shrink-0" strokeWidth={1.8} /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Action row */}
            <div className="mt-10">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]/60 mb-4">{T.result_next}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <a
                  href={waLink("Hi Dr. Shantanu, I just used your Eye Symptom Checker and would like to book an appointment.")}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => logCta("whatsapp")}
                  className="rounded-2xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/[0.06] p-4 flex flex-col gap-2 hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid="cta-whatsapp"
                >
                  <div className="flex items-center gap-3">
                    <MessageCircle size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
                    <span className="text-[14px] font-medium">{T.cta_wa_title}</span>
                  </div>
                  <span className="text-[11.5px] text-[hsl(var(--foreground))]/65 leading-relaxed">
                    {T.cta_wa_sub}
                  </span>
                </a>
                <Link
                  to="/contact"
                  onClick={() => logCta("book")}
                  className="rounded-2xl border border-border p-4 flex items-center gap-3 hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid="cta-book"
                >
                  <CalendarCheck size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
                  <span className="text-[14px] font-medium">{T.cta_book}</span>
                </Link>
                <a
                  href={`tel:${(CLINIC.phone || "").replace(/\s/g, "")}`}
                  onClick={() => logCta("call")}
                  className="rounded-2xl border border-border p-4 flex items-center gap-3 hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid="cta-call"
                >
                  <Phone size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
                  <span className="text-[14px] font-medium">{T.cta_call}</span>
                </a>
                <a
                  href={CLINIC.mapLink}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => logCta("map")}
                  className="rounded-2xl border border-border p-4 flex items-center gap-3 hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid="cta-map"
                >
                  <MapPin size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
                  <span className="text-[14px] font-medium">{T.cta_map}</span>
                </a>
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-border bg-[hsl(var(--foreground))]/[0.02] px-5 py-4 flex items-start gap-3">
              <Info size={16} className="mt-1 text-[hsl(var(--accent))] shrink-0" strokeWidth={1.8} />
              <p className="text-[12.5px] leading-relaxed text-[hsl(var(--foreground))]/75">
                {DISCLAIMER}
                <span className="block mt-2 text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50">{CREDIT_LINE}</span>
              </p>
            </div>

            <div className="mt-10 flex justify-center">
              <button onClick={restart} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--foreground))]" data-testid="btn-restart">
                <RotateCcw size={13} strokeWidth={1.8} /> {T.restart}
              </button>
            </div>
          </div>
        </section>
      </main>
    );
  }

  /* ─────────────────────────── WIZARD STEPS ─────────────────────────── */
  return (
    <main className="pb-24" data-testid="symptom-checker-wizard">
      <div className="container-page pt-10">
        <div className="max-w-3xl mx-auto mb-10">
          <ProgressRail current={step} total={TOTAL_STEPS} />
        </div>

        {step === 1 && (
          <StepShell
            stepNumber={1}
            totalSteps={TOTAL_STEPS}
            title={T.step1_title}
            hint={T.step1_hint}
            onNext={() => goto(2)}
            nextDisabled={!state.age_band}
            T={T}
          >
            <div className="grid sm:grid-cols-2 gap-3">
              {AGE_BANDS.map((a) => (
                <OptionCard
                  key={a.key}
                  selected={state.age_band === a.key}
                  onClick={() => patch({ age_band: a.key })}
                  label={tr(a)}
                  testid={`opt-age-${a.key}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 2 && (
          <StepShell
            stepNumber={2}
            totalSteps={TOTAL_STEPS}
            title={T.step2_title}
            hint={T.step2_hint}
            onBack={() => goto(1)}
            onNext={() => goto(3)}
            nextLabel={state.gender ? T.continue : T.skip}
            T={T}
          >
            <div className="grid sm:grid-cols-2 gap-3">
              {GENDERS.map((g) => (
                <OptionCard
                  key={g.key}
                  selected={state.gender === g.key}
                  onClick={() => patch({ gender: g.key })}
                  label={tr(g)}
                  testid={`opt-gender-${g.key}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 3 && (
          <StepShell
            stepNumber={3}
            totalSteps={TOTAL_STEPS}
            title={T.step3_title}
            onBack={() => goto(2)}
            onNext={() => goto(4)}
            nextDisabled={!state.eye_side}
            T={T}
          >
            <div className="grid sm:grid-cols-3 gap-3">
              {EYE_SIDES.map((e) => (
                <OptionCard
                  key={e.key}
                  selected={state.eye_side === e.key}
                  onClick={() => patch({ eye_side: e.key })}
                  label={tr(e)}
                  testid={`opt-eye-${e.key}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 4 && (
          <StepShell
            stepNumber={4}
            totalSteps={TOTAL_STEPS}
            title={T.step4_title}
            hint={T.step4_hint}
            onBack={() => goto(3)}
            onNext={() => goto(5)}
            nextDisabled={!state.main_symptom}
            T={T}
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {SYMPTOMS.map((s) => (
                <OptionCard
                  key={s.slug}
                  selected={state.main_symptom === s.slug}
                  onClick={() => patch({ main_symptom: s.slug, followups: {} })}
                  icon={s.icon}
                  label={tr(s)}
                  danger={s.danger}
                  testid={`opt-symptom-${s.slug}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 5 && (
          <StepShell
            stepNumber={5}
            totalSteps={TOTAL_STEPS}
            title={T.step5_title}
            hint={T.step5_hint}
            onBack={() => goto(4)}
            onNext={() => goto(6)}
            T={T}
          >
            {activeSymptomFollowups.length === 0 && (
              <p className="text-[14px] text-[hsl(var(--foreground))]/70">{T.step5_noneMsg}</p>
            )}
            <div className="space-y-8">
              {activeSymptomFollowups.map((f) => (
                <div key={f.key} data-testid={`followup-${f.key}`}>
                  <div className="text-[15px] font-medium text-[hsl(var(--foreground))] mb-3">{trQ(f)}</div>
                  <div className="flex flex-wrap gap-2">
                    {f.opts.map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => patchFollowup(f.key, o.v)}
                        className={`px-4 py-2 rounded-full text-[13px] border transition-colors
                          ${state.followups[f.key] === o.v
                            ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                            : "bg-transparent border-border text-[hsl(var(--foreground))]/80 hover:border-[hsl(var(--foreground))]/40"}`}
                        data-testid={`followup-${f.key}-${o.v}`}
                      >
                        {tr(o)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Voice / free-text: "Anything else in your own words?" */}
              <div data-testid="notes-block" className="pt-4 border-t border-border/60">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[15px] font-medium text-[hsl(var(--foreground))]">{T.notes_title}</div>
                  {recognitionRef.current && (
                    <button
                      type="button"
                      onClick={toggleMic}
                      data-testid="btn-mic"
                      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] uppercase tracking-[0.14em] border transition-colors
                        ${listening
                          ? "bg-red-500/10 text-red-500 border-red-500/40 animate-pulse"
                          : "bg-transparent text-[hsl(var(--foreground))]/70 border-border hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]"}`}
                      aria-label={listening ? T.mic_listening : T.mic_speak}
                    >
                      {listening ? <MicOff size={14} strokeWidth={1.8} /> : <Mic size={14} strokeWidth={1.8} />}
                      {listening ? T.mic_listening : T.mic_speak}
                    </button>
                  )}
                </div>
                <textarea
                  data-testid="notes-textarea"
                  value={state.notes}
                  onChange={(e) => patch({ notes: e.target.value })}
                  placeholder={T.notes_placeholder}
                  rows={3}
                  className="w-full rounded-2xl border border-border bg-transparent p-4 text-[14px] leading-relaxed text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--foreground))]/45 focus:outline-none focus:border-[hsl(var(--accent))] transition-colors"
                />
                <p className="mt-2 text-[11.5px] text-[hsl(var(--foreground))]/55">
                  {recognitionRef.current ? T.notes_footer_supported : T.notes_footer_unsupported}
                </p>
              </div>

              {/* Optional photo upload */}
              <div data-testid="photo-block" className="pt-6 border-t border-border/60">
                <div className="text-[15px] font-medium text-[hsl(var(--foreground))] mb-3">
                  {T.photo_title}
                </div>
                <p className="text-[12.5px] text-[hsl(var(--foreground))]/60 mb-4">
                  {T.photo_desc}
                </p>
                {state.photo_url ? (
                  <div className="relative inline-block">
                    <img src={state.photo_url} alt="Uploaded eye photo" className="w-40 h-40 object-cover rounded-xl border border-border" />
                    <button
                      type="button"
                      onClick={() => patch({ photo_url: null })}
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-background border border-border flex items-center justify-center hover:border-red-500 hover:text-red-500 transition-colors"
                      aria-label="Remove photo"
                      data-testid="btn-photo-remove"
                    >
                      <XIcon size={13} strokeWidth={2} />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="photo-input"
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] border cursor-pointer transition-colors
                      ${uploadingPhoto ? "opacity-50" : "border-border hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]"}`}
                    data-testid="btn-photo-upload"
                  >
                    <Camera size={14} strokeWidth={1.8} />
                    {uploadingPhoto ? T.photo_uploading : T.photo_attach}
                  </label>
                )}
                <input
                  id="photo-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  disabled={uploadingPhoto}
                />
              </div>
            </div>
          </StepShell>
        )}

        {step === 6 && (
          <StepShell
            stepNumber={6}
            totalSteps={TOTAL_STEPS}
            title={T.step6_title}
            hint={T.step6_hint}
            onBack={() => goto(5)}
            onNext={submit}
            nextLabel={loading ? T.running : T.seeResult}
            nextDisabled={loading}
            T={T}
          >
            <div className="grid sm:grid-cols-2 gap-3">
              {MEDICAL_HISTORY.map((h) => {
                const active = state.medical_history.includes(h.key);
                return (
                  <button
                    key={h.key}
                    type="button"
                    onClick={() => {
                      if (h.key === "none") {
                        patch({ medical_history: active ? [] : ["none"] });
                      } else {
                        const next = active
                          ? state.medical_history.filter((k) => k !== h.key)
                          : [...state.medical_history.filter((k) => k !== "none"), h.key];
                        patch({ medical_history: next });
                      }
                    }}
                    className={`text-left w-full rounded-2xl px-5 py-4 border transition-colors flex items-center gap-3
                      ${active
                        ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.05]"
                        : "border-border hover:border-[hsl(var(--foreground))]/30"}`}
                    data-testid={`opt-history-${h.key}`}
                  >
                    <span className={`w-5 h-5 rounded-md border flex items-center justify-center
                      ${active ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))]" : "border-[hsl(var(--foreground))]/30"}`}>
                      {active && <Check size={13} className="text-white" strokeWidth={2.4} />}
                    </span>
                    <span className="text-[14.5px]">{tr(h)}</span>
                  </button>
                );
              })}
            </div>
          </StepShell>
        )}
      </div>

      <div className="container-page mt-16">
        <div className="max-w-3xl mx-auto rounded-2xl border border-border bg-[hsl(var(--foreground))]/[0.02] px-5 py-4 flex items-start gap-3">
          <Info size={16} className="mt-1 text-[hsl(var(--accent))] shrink-0" strokeWidth={1.8} />
          <p className="text-[12.5px] leading-relaxed text-[hsl(var(--foreground))]/75">
            {DISCLAIMER_LONG}
            <span className="block mt-2 text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--foreground))]/50">{CREDIT_LINE}</span>
          </p>
        </div>
      </div>
    </main>
  );
};

export default SymptomChecker;
